import { ComponentType } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import {
  GenericServiceValue,
  PersonalDoctorValue,
  PharmacologicalTreatmentValue,
  Prescription,
  PrescriptionFormValue,
  PrescriptionTypes,
  deletePrescriptionDraft,
} from '@lib/prescriptions/data-access';
import { PrescriptionIconComponent } from '@lib/prescriptions/ui/components/commons/icon/prescription-icon.component';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { ListItemExpandedTemplateComponent } from '@lib/shared-ui/commons/list-item-expanded-template/list-item-expanded-template.component';
import { ListItemNeutralButtonComponent } from '@lib/shared-ui/commons/list-item-neutral-button/list-item-neutral-button.component';
import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

import { MedicationPrescriptionFormModalComponent } from '../../modals/medication-prescription-form-modal/medication-prescription-form-modal.component';
import { PersonalDoctorPrescriptionFormModalComponent } from '../../modals/personal-doctor-prescription-form-modal/personal-doctor-prescription-form-modal.component';
import { ServicePrescriptionFormModalComponent } from '../../modals/service-prescription-form-modal/service-prescription-form-modal.component';

@Component({
  selector: 'prescriptions-prescription-draft-list-item',
  standalone: true,
  imports: [
    NgClass,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    TranslatePipe,
    ListItemExpandedTemplateComponent,
    ListItemNeutralButtonComponent,
    StatusComponent,
    PrescriptionIconComponent,
  ],
  templateUrl: './prescription-draft-list-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionDraftListItemComponent {
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  draft = input.required<PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>>();
  memberId = input.required<string>();
  guaranteeApplicationId = input<string | null>(null);

  protected readonly displayName = computed<string>(() => {
    if (this.draft().type === PrescriptionTypes.PersonalDoctorAppointment) {
      const metadata = this.draft().metadata as PersonalDoctorValue;
      return metadata.doctorName || this.draft().name;
    }
    return this.draft().name;
  });

  protected readonly now = new Date();
  protected readonly formatDate = formatDate;
  protected readonly startDate = computed<Date>(() => new Date(this.draft().startDate));
  protected readonly endDate = computed<Date>(() => new Date(this.draft().endDate));

  protected readonly quantity = computed<string | null>(() => {
    const metadata = this.draft().metadata;
    if (this.draft().type === PrescriptionTypes.Drugs) {
      const treatmentDose = (metadata as PharmacologicalTreatmentValue).treatmentDose;
      return treatmentDose ? `${treatmentDose} од.` : null;
    }
    const quantity = (metadata as GenericServiceValue).quantity;
    return quantity ? `${quantity}` : null;
  });

  protected openEditModal(): void {
    let componentToOpen: ComponentType<unknown>;
    switch (this.draft().type) {
      case PrescriptionTypes.Drugs:
        componentToOpen = MedicationPrescriptionFormModalComponent;
        break;
      case PrescriptionTypes.PersonalDoctorAppointment:
        componentToOpen = PersonalDoctorPrescriptionFormModalComponent;
        break;
      default:
        componentToOpen = ServicePrescriptionFormModalComponent;
        break;
    }

    this.dialog.open(componentToOpen, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      data: {
        prescription: this.draft() as unknown as Prescription,
        prescriptionType: this.draft().type,
        memberId: this.memberId(),
        guaranteeApplicationId: this.guaranteeApplicationId(),
      },
    });
  }

  protected deleteDraft(): void {
    this.store.dispatch(deletePrescriptionDraft({ id: this.draft().id }));
  }
}
